import { useEffect, useRef } from "react";

/**
 * Confirmation for a destructive action, listing what it will destroy.
 *
 * The prototype deleted a transition — cascading to its actions, its rules and their conditions —
 * with no warning at all. The consequences are passed in and rendered, because "are you sure?"
 * without saying what is lost is not a confirmation.
 *
 * Accessibility here is the part that was missing entirely before: aria-modal, a labelled
 * heading, initial focus on the safe action, Escape to cancel, and a focus trap so tabbing
 * cannot wander into the page behind.
 */
export function ConfirmDialog({
    title,
    consequences,
    note,
    confirmLabel,
    onConfirm,
    onCancel,
    isPending,
}: {
    title: string;
    consequences: string[];
    /** A consequence that is not a deletion — e.g. a condition left orphaned. */
    note?: string | null;
    confirmLabel: string;
    onConfirm: () => void;
    onCancel: () => void;
    isPending?: boolean;
}) {
    const dialogRef = useRef<HTMLDivElement>(null);
    const cancelRef = useRef<HTMLButtonElement>(null);

    // Focus the safe action, not the destructive one: a stray Enter should cancel.
    useEffect(() => {
        cancelRef.current?.focus();
    }, []);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                event.preventDefault();
                onCancel();
                return;
            }

            if (event.key !== "Tab") {
                return;
            }

            const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
                "button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])",
            );
            if (!focusable?.length) {
                return;
            }

            const first = focusable[0];
            const last = focusable[focusable.length - 1];

            // Wrap at both ends, so focus stays inside the dialog.
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [onCancel]);

    return (
        // The backdrop click is a convenience dismissal only; Escape and the Cancel button are
        // the accessible routes, both wired above.
        <div className="aer-wf-backdrop" onClick={onCancel}>
            <div
                className="aer-wf-dialog"
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby="aer-wf-dialog-title"
                onClick={(event) => event.stopPropagation()}
            >
                <h3 id="aer-wf-dialog-title">{title}</h3>

                {consequences.length > 0 && (
                    <>
                        <p>This will also delete:</p>
                        <ul className="aer-wf-dialog__consequences">
                            {consequences.map((consequence) => (
                                <li key={consequence}>{consequence}</li>
                            ))}
                        </ul>
                    </>
                )}

                {note && <p className="aer-wf-dialog__note">{note}</p>}

                <p className="aer-wf-dialog__warning">This cannot be undone.</p>

                <div className="aer-wf-dialog__actions">
                    <button
                        type="button"
                        ref={cancelRef}
                        className="aer-wf-button aer-wf-button--secondary"
                        onClick={onCancel}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="aer-wf-button aer-wf-button--danger"
                        onClick={onConfirm}
                        disabled={isPending}
                    >
                        {isPending ? "Deleting…" : confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}
